<template>
    <div>
        <h2 class="mb-1">{{ $t("settings.account.notifications.header") }}</h2>
        <h3 class="mb-1">{{ $t("settings.account.notifications.browser.header") }}</h3>

        <template v-if="!notificationsSupported">
            <b-alert show variant="warning">
                <div class="alert-body d-flex">
                    <font-awesome-icon class="mr-50" icon="exclamation-triangle" size="lg" />
                    <span class="ml-25">{{ $t("settings.account.no-web-notification-supported") }}</span>
                </div>
            </b-alert>
        </template>
        <template v-else>
            <div v-show="notificationsAllowed !== 'granted'">
                <b-alert show variant="warning">
                    <div v-if="notificationsAllowed === 'prompt'" class="alert-body">
                        <font-awesome-icon class="mr-50" icon="exclamation-triangle" size="lg" />
                        <span class="ml-25">
                            {{ $t("settings.account.web-notification-permission-prompt") }}
                        </span>
                        <br /><br />

                        <b-button variant="primary" @click.prevent="showNotification">
                            {{ $t("settings.account.request-permission") }}
                        </b-button>
                    </div>

                    <div v-if="notificationsAllowed === 'denied'" class="alert-body">
                        <p class="ml-25">
                            <font-awesome-icon class="mr-50" icon="exclamation-triangle" size="lg" />
                            {{ $t("settings.account.web-notification-permission-denied") }}
                        </p>
                    </div>
                </b-alert>
            </div>

            <template v-show="notificationsAllowed === 'granted'">
                <b-form-group
                    id="fieldset-sound-notifications-preview"
                    :label="$t('settings.account.notifications.browser.preview')"
                    class="d-none d-md-block"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                >
                    <b-button variant="primary" @click.prevent="showNotification">
                        {{ $t("settings.account.web-notification-test") }}
                    </b-button>
                </b-form-group>

                <b-form-group
                    id="fieldset-browser-notifications-new-conversation"
                    :description="$t('settings.account.notifications.new-conversation.description')"
                    :label="$t('settings.account.notifications.new-conversation.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="notifications"
                >
                    <b-form-checkbox
                        v-model="getSettingByName('notifyConversationUnassigned', '0').value"
                        inline
                        name="browser-notifications-new-conversation"
                        switch
                        unchecked-value="0"
                        value="1"
                    />
                </b-form-group>

                <b-form-group
                    id="fieldset-browser-notifications-new-message-assigned"
                    :description="$t('settings.account.notifications.new-message-assigned.description')"
                    :label="$t('settings.account.notifications.new-message-assigned.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="notifications"
                >
                    <b-form-checkbox
                        v-model="getSettingByName('notifyIncomingMessage', '0').value"
                        inline
                        name="browser-notifications-new-message-assigned"
                        switch
                        unchecked-value="0"
                        value="1"
                    />
                </b-form-group>

                <b-form-group
                    id="fieldset-browser-notifications-new-message-unassigned"
                    :description="$t('settings.account.notifications.new-message-unassigned.description')"
                    :label="$t('settings.account.notifications.new-message-unassigned.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="notifications"
                >
                    <b-form-checkbox
                        v-model="getSettingByName('notifyIncomingMessageNew', '0').value"
                        inline
                        name="browser-notifications-new-message-unassigned"
                        switch
                        unchecked-value="0"
                        value="1"
                    />
                </b-form-group>

                <b-form-group
                    id="fieldset-browser-notifications-conversation-assigned"
                    :description="$t('settings.account.notifications.conversation-assigned.description')"
                    :label="$t('settings.account.notifications.conversation-assigned.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="notifications"
                >
                    <b-form-checkbox
                        v-model="getSettingByName('notifyConversationAssigned', '0').value"
                        inline
                        name="browser-notifications-conversation-assigned"
                        switch
                        unchecked-value="0"
                        value="1"
                    />
                </b-form-group>
            </template>
        </template>

        <br />

        <h3 class="mb-1">{{ $t("settings.account.notifications.sound.header") }}</h3>

        <b-form-group
            id="fieldset-sound-notifications-preview"
            :label="$t('settings.account.notifications.sound.preview')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
        >
            <b-button type="button" variant="primary" @click="playNotificationAudio()">
                <font-awesome-icon :icon="['far', 'circle-play']" class="cursor-pointer" size="1x" />
            </b-button>
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-new-conversation"
            :description="$t('settings.account.notifications.new-conversation.description')"
            :label="$t('settings.account.notifications.new-conversation.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyConversationUnassigned', '0').value"
                inline
                name="sound-notifications-new-conversation"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-new-message-assigned"
            :description="$t('settings.account.notifications.new-message-assigned.description')"
            :label="$t('settings.account.notifications.new-message-assigned.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyIncomingMessage', '0').value"
                inline
                name="sound-notifications-new-message-assigned"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-new-message-unassigned"
            :description="$t('settings.account.notifications.new-message-unassigned.description')"
            :label="$t('settings.account.notifications.new-message-unassigned.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyIncomingMessageNew', '0').value"
                inline
                name="sound-notifications-new-message-unassigned"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-conversation-assigned"
            :description="$t('settings.account.notifications.conversation-assigned.description')"
            :label="$t('settings.account.notifications.conversation-assigned.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyConversationAssigned', '0').value"
                inline
                name="sound-notifications-conversation-assigned"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>
        <br />

        <h3 class="mb-1">{{ $t("settings.account.notifications.email.header") }}</h3>
        <p>
            {{
                $t("settings.account.notifications.email.destination", {
                    email: userData.email,
                })
            }}
        </p>

        <b-form-group
            id="fieldset-email-notifications-new-conversation"
            :description="$t('settings.account.notifications.new-conversation.description')"
            :label="$t('settings.account.notifications.new-conversation.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('sendEmailNotificationOnNewConversation', '0').value"
                inline
                name="email-notifications-new-conversation"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-email-notifications-new-message-assigned"
            :description="$t('settings.account.notifications.new-message-assigned.description')"
            :label="$t('settings.account.notifications.new-message-assigned.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('sendEmailNotificationOnReplyInAssigned', '0').value"
                inline
                name="email-notifications-new-message-assigned"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-email-notifications-conversation-assigned"
            :description="$t('settings.account.notifications.conversation-assigned.description')"
            :label="$t('settings.account.notifications.conversation-assigned.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('sendEmailNotificationConversationAssigned', '0').value"
                inline
                name="email-notifications-conversation-assigned"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <hr />

        <h3 class="mb-1">{{ $t("settings.account.notifications.teamChats.header") }}</h3>

        <b-form-group
            id="fieldset-browser-notifications-new-teamchat"
            :description="$t('settings.account.notifications.teamChats.newChat.description')"
            :label="$t('settings.account.notifications.teamChats.newChat.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('notifyTeamchatStarted').value"
                inline
                name="browser-notifications-new-teamchat"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-browser-notifications-new-teamchat-message"
            :description="$t('settings.account.notifications.teamChats.newMessage.description')"
            :label="$t('settings.account.notifications.teamChats.newMessage.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('notifyTeamchatMessage').value"
                inline
                name="browser-notifications-new-teamchat-message"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-new-teamchat"
            :description="$t('settings.account.notifications.teamChats.newChatSound.description')"
            :label="$t('settings.account.notifications.teamChats.newChatSound.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyTeamchatStarted').value"
                inline
                name="sound-notifications-new-teamchat"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-new-teamchat-message"
            :description="$t('settings.account.notifications.teamChats.newMessageSound.description')"
            :label="$t('settings.account.notifications.teamChats.newMessageSound.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('soundNotifyTeamchatMessage').value"
                inline
                name="sound-notifications-new-teamchat-message"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-email-notifications-new-teamchat"
            :description="$t('settings.account.notifications.teamChats.newChatEmail.description')"
            :label="$t('settings.account.notifications.teamChats.newChatEmail.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('emailNotifyTeamchatStarted').value"
                inline
                name="email-notifications-new-teamchat"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-email-notifications-new-teamchat-message"
            :description="$t('settings.account.notifications.teamChats.newMessageEmail.description')"
            :label="$t('settings.account.notifications.teamChats.newMessageEmail.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="notifications"
        >
            <b-form-checkbox
                v-model="getSettingByName('emailNotifyTeamchatMessage').value"
                inline
                name="email-notifications-new-teamchat-message"
                switch
                unchecked-value="0"
                value="1"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-sound-notifications-teamchat-preview"
            :label="$t('settings.account.notifications.sound.preview')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
        >
            <b-button type="button" variant="primary" @click="playNotificationAudio('notification_teamchat')">
                <font-awesome-icon :icon="['far', 'circle-play']" class="cursor-pointer" size="1x" />
            </b-button>
        </b-form-group>
        <br />

        <hr />
    </div>
</template>
<script>
import { BCard, BFormGroup, BFormCheckbox, BButton, BRow, BCol, BAlert } from "bootstrap-vue";
import useUserAccounts from "../useUserAccounts";
import { usePermission } from "@vueuse/core";
import useWebNotifications from "@/composables/useWebNotifications";
import i18n from "@/libs/i18n";

export default {
    components: {
        BFormGroup,
        BFormCheckbox,
        BCard,
        BButton,
        BRow,
        BCol,
        BAlert,
    },
    props: {
        userData: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const { vueUseWebNotification } = useWebNotifications();
        const { isSupported, show } = vueUseWebNotification({
            title: i18n.t("settings.account.web-notification-success"),
        });

        const { getSettingByName } = useUserAccounts(props.userData);

        return {
            getSettingByName,
            notificationsAllowed: usePermission("notifications"),
            notificationsSupported: isSupported,
            showNotification: show, // Will also request for permission.
        };
    },
    methods: {
        playNotificationAudio(notificationFile = "notification") {
            // NOTE: in order for this audio promise to play a user needs to have had an interaction (click, keystroke, etc.) with the document
            // Otherwise the browser restrictions for autoplaying of media will kick in and no audio would be played.
            // chrome: https://developer.chrome.com/blog/autoplay/
            // firefox: https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide#autoplay_availability
            const audio = new Audio(`${process.env.VUE_APP_MFE_STATIC_ENDPOINT}/audio/${notificationFile}.mp3`);
            audio.play();
        },
        requestPermission() {
            Notification.requestPermission().then((result) => {
                this.notificationsAllowed = result === "granted";
            });
        },
    },
};
</script>
